// src/components/UsernameSelectionModal.jsx
import React, { useState, useRef, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Filter } from 'bad-words';
import { db, functions } from '../firebase';
import { generateUsername } from '../utils/usernameGenerator'; // Import from the new utility file

function UsernameSelectionModal({ 
  currentUser, 
  setShowUsernameModal, 
  setLibraryUsername, 
  exportToLibraryPattern, 
  exportPatternToLibrary, 
  generatedUsername, 
  setGeneratedUsername,
  setExportToLibraryPattern
}) {
  const [usernameType, setUsernameType] = useState("custom");
  const [customUsername, setCustomUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  // Add this to manage local state within the component
  const [localGeneratedUsername, setLocalGeneratedUsername] = useState("");
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Only run once, using a ref to track initialization
    if (!hasInitialized.current) {
      console.log("UsernameSelectionModal mounted");
      try {
        const generated = generateUsername();
        console.log("Generated username:", generated);
        // Set the local state first
        setLocalGeneratedUsername(generated);
        // Don't update parent state here to avoid re-renders
        hasInitialized.current = true;
      } catch (error) {
        console.error("Error generating username:", error);
      }
    }
  }, []);

  const checkUsernameAvailability = async (username) => {
    if (!username || username.trim() === "") return false;

    try {
      // Finding #2: username uniqueness runs server-side now (no users list).
      const checkFn = httpsCallable(functions, "isUsernameAvailable");
      const res = await checkFn({ username: username.trim() });

      // Available iff the function found no existing holder.
      return !!(res.data && res.data.available);
    } catch (error) {
      console.error("Error checking username availability:", error);
      return false; // Default to unavailable on error
    }
  };

  const handleSaveUsername = async () => {
    const filter = new Filter();

    if (customUsername && filter.isProfane(customUsername)) {
      setUsernameError("Please choose an appropriate username");
      return;
    }

    const usernameToCheck =
      usernameType === "custom" ? customUsername : localGeneratedUsername;
    console.log("Username to check:", usernameToCheck);

    // Validate username
    if (!usernameToCheck || usernameToCheck.length < 3) {
      console.log("Username validation failed");
      setUsernameError("Username must be at least 3 characters");
      return;
    }

    try {
      // Check if username is available by querying usernames collection
      const usernamesRef = collection(db, "usernames");
      const q = query(usernamesRef, where("username", "==", usernameToCheck));
      const querySnapshot = await getDocs(q);

      // If the querySnapshot is not empty, username is taken
      const isAvailable = querySnapshot.empty;
      console.log("Username available:", isAvailable);

      if (!isAvailable) {
        setUsernameError("This username is already taken");
        return;
      }

      // Save username to state and user profile
      setLibraryUsername(usernameToCheck);
      console.log("Set library username to:", usernameToCheck);

      if (currentUser) {
        // Save username to user's profile in Firestore
        try {
          const userRef = doc(db, "users", currentUser.uid);
          await updateDoc(userRef, {
            libraryUsername: usernameToCheck,
          });

          // Also save in a collection to ensure uniqueness
          const usernameDocRef = doc(db, "usernames", currentUser.uid);
          await setDoc(usernameDocRef, {
            username: usernameToCheck,
            userId: currentUser.uid,
          });
          console.log("Username saved to FlowFrame");
        } catch (error) {
          console.error("Error saving username:", error);
          setUsernameError("Failed to save username. Please try again.");
          return;
        }
      }

      // Clear any previous errors
      setUsernameError("");

      // Close modal and continue with export
      setShowUsernameModal(false);
      if (exportToLibraryPattern) {
        exportPatternToLibrary(exportToLibraryPattern);
      }
    } catch (error) {
      console.error("Error checking username availability:", error);
      setUsernameError(
        "An error occurred while checking username. Please try again."
      );
    }
  };

  const handleGenerateNewUsername = () => {
    try {
      const newUsername = generateUsername();
      console.log("New generated username:", newUsername);
      setLocalGeneratedUsername(newUsername);
      setGeneratedUsername(newUsername);
    } catch (error) {
      console.error("Error generating new username:", error);
    }
  };

  const handleUsernameTypeChange = (type) => {
    console.log("Username type changed to:", type);
    setUsernameType(type);
  };

  const handleCustomUsernameChange = (e) => {
    console.log("Custom username changed to:", e.target.value);
    setCustomUsername(e.target.value);
  };

  console.log("Current state:", {
    usernameType,
    customUsername,
    generatedUsername,
    isChecking,
    usernameError,
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div
        className="bg-white rounded-lg p-6 max-w-md w-full z-[10000]"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <h2 className="text-xl font-bold mb-4">
          Choose Your Library Username
        </h2>
        <p className="text-gray-600 mb-4">
          This username will be displayed when you share patterns to the
          FlowFrame Library.
        </p>

        <div className="mb-4">
          <div className="flex items-center mb-2">
            <input
              type="radio"
              id="customUsername"
              name="usernameType"
              value="custom"
              checked={usernameType === "custom"}
              onChange={() => {
                handleUsernameTypeChange("custom");
              }}
              className="mr-2"
            />
            <label
              htmlFor="customUsername"
              onClick={() => {
                handleUsernameTypeChange("custom");
              }}
            >
              Create my own username
            </label>
          </div>

          {usernameType === "custom" && (
            <input
              type="text"
              value={customUsername}
              onChange={handleCustomUsernameChange}
              onClick={(e) => {
                e.stopPropagation();
              }}
              placeholder="Enter a unique username"
              className="w-full p-2 border rounded"
            />
          )}
        </div>

        <div className="mb-4">
          <div className="flex items-center mb-2">
            <input
              type="radio"
              id="generatedUsername"
              name="usernameType"
              value="generated"
              checked={usernameType === "generated"}
              onChange={() => {
                handleUsernameTypeChange("generated");
              }}
              className="mr-2"
            />
            <label
              htmlFor="generatedUsername"
              onClick={() => {
                handleUsernameTypeChange("generated");
              }}
            >
              Use a musical username
            </label>
          </div>

          {usernameType === "generated" && (
            <div className="flex items-center">
              <div className="border p-2 rounded flex-1 bg-gray-50">
                {localGeneratedUsername}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleGenerateNewUsername();
                }}
                className="ml-2 p-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                🔄
              </button>
            </div>
          )}
        </div>

        {usernameError && (
          <p className="text-red-500 text-sm mb-4">{usernameError}</p>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowUsernameModal(false);
              setExportToLibraryPattern(null);
            }}
            className="px-4 py-2 border rounded hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleSaveUsername();
            }}
            disabled={isChecking}
            className="px-4 py-2 bg-accent-600 text-white rounded hover:bg-accent-700 disabled:opacity-50"
          >
            {isChecking ? "Checking..." : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UsernameSelectionModal;